import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, DollarSign, ClipboardList, AlertTriangle, Receipt, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast, PageHeader, StatusBadge, SkeletonRow, SkeletonCard, Input, FilterTabs, EmptyState } from '../components/ui';
import { fmtMoney } from '../utils/currency';

const PERIODS = [
  { value: 'today',  label: 'Today' },
  { value: 'week',   label: 'Last 7 days' },
  { value: 'month',  label: 'This month' },
  { value: 'custom', label: 'Custom' },
];

const PERIOD_LABEL = {
  today: 'today',
  week: 'last 7 days',
  month: 'this month',
  custom: 'selected range',
};

const toDateStr = (d) => d.toISOString().slice(0, 10);

const tintClass = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger:  'bg-danger-50 text-danger-600',
};

const Dashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState(toDateStr(new Date()));
  const [customEnd, setCustomEnd] = useState(toDateStr(new Date()));
  const [stats, setStats] = useState({ totalOrders: 0, totalSales: 0, activeOrders: 0, lowStock: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  // Resolve the selected period into concrete start/end dates. "today" is
  // left as null so the request relies on tzOffset instead of a plain UTC
  // date string — that's the only window where the extra precision matters
  // (it has to agree exactly with the Day Close / Z-report).
  const resolveRange = () => {
    const now = new Date();
    if (period === 'today') return { startDate: null, endDate: null };
    if (period === 'week') {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { startDate: toDateStr(start), endDate: toDateStr(now) };
    }
    if (period === 'month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toDateStr(start), endDate: toDateStr(now) };
    }
    return { startDate: customStart, endDate: customEnd };
  };

  useEffect(() => {
    fetchData();
  }, [period, customStart, customEnd, user]);

  const fetchData = async () => {
    setLoading(true);
    const headers = { 'x-auth-token': localStorage.getItem('token') };
    const { startDate, endDate } = resolveRange();
    try {
      const dashParams = startDate && endDate
        ? { startDate, endDate }
        : { tzOffset: new Date().getTimezoneOffset() };

      const requests = [
        axios.get('/api/analytics/dashboard', { headers, params: dashParams }),
        axios.get('/api/orders?limit=5', { headers }),
      ];
      // Trend needs an explicit range either way (sales-by-period has no
      // "today" shortcut) — for "today" just show today alone.
      const trendStart = startDate || toDateStr(new Date());
      const trendEnd = endDate || toDateStr(new Date());
      requests.push(
        axios.get('/api/analytics/sales-by-period', { headers, params: { startDate: trendStart, endDate: trendEnd, period: 'daily' } })
      );

      const [dashRes, ordersRes, trendRes] = await Promise.all(requests);
      setStats({
        totalOrders: dashRes.data.todayOrders || 0,
        totalSales: dashRes.data.todaySales || 0,
        activeOrders: dashRes.data.activeOrders || 0,
        lowStock: dashRes.data.lowStockItems || 0
      });
      setRecentOrders(ordersRes.data || []);
      setTrend(trendRes.data || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      toast.error(err.response?.data?.msg || 'Could not load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const avgOrderValue = stats.totalOrders > 0 ? stats.totalSales / stats.totalOrders : 0;
  const periodLabel = PERIOD_LABEL[period];

  const STAT_DEFS = [
    { key: 'totalSales',   label: `Revenue (${periodLabel})`,  color: 'success', Icon: DollarSign,
      fmt: () => fmtMoney(stats.totalSales, true) },
    { key: 'totalOrders',  label: `Orders (${periodLabel})`,   color: 'primary', Icon: ShoppingCart,
      fmt: () => stats.totalOrders.toString() },
    { key: 'avgOrder',     label: 'Avg. order value',          color: 'primary', Icon: Receipt,
      fmt: () => fmtMoney(avgOrderValue, true) },
    { key: 'activeOrders', label: 'Active orders right now',   color: 'warning', Icon: ClipboardList,
      fmt: () => stats.activeOrders.toString() },
    { key: 'lowStock',     label: 'Low stock items',           color: 'danger',  Icon: AlertTriangle,
      fmt: () => stats.lowStock.toString() },
  ];

  const maxTrend = trend.length ? Math.max(...trend.map(t => t.sales || 0)) : 0;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Revenue, orders, and stock at a glance — pick a range to look back further."
      />

      {/* Period filter */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 mb-6 flex flex-wrap items-end gap-3">
        <FilterTabs value={period} onChange={setPeriod} options={PERIODS} />
        {period === 'custom' && (
          <>
            <div className="w-40">
              <Input label="Start date" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            </div>
            <div className="w-40">
              <Input label="End date" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </div>
          </>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {STAT_DEFS.map(({ key, label, color, Icon, fmt }) => (
          <div key={key} className="bg-white rounded-lg border border-neutral-200 shadow-card p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tintClass[color]}`}>
                <Icon size={18} />
              </div>
            </div>
            <p className="text-2xl font-bold text-neutral-900 tracking-tight tabular-nums">
              {loading ? '…' : fmt()}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales trend */}
        <div className="lg:col-span-1 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <TrendingUp size={14} className="text-neutral-500" />
            <h2 className="text-sm font-semibold text-neutral-800">Sales trend</h2>
          </div>
          <div className="p-5">
            {loading ? (
              <SkeletonCard />
            ) : trend.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No sales in this range" compact />
            ) : (
              <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {trend.map((t, idx) => {
                  const pct = maxTrend > 0 ? ((t.sales || 0) / maxTrend) * 100 : 0;
                  return (
                    <li key={idx}>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-xs font-medium text-neutral-700">{t._id}</span>
                        <span className="text-xs font-semibold tabular-nums">{fmtMoney(t.sales || 0, true)}</span>
                      </div>
                      <div className="w-full bg-neutral-100 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-success-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Recent orders */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-800">Recent orders</h2>
            <span className="text-xs text-neutral-500">{recentOrders.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="app-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonRow key={`s-${i}`} cols={4} />
                ))}
                {!loading && recentOrders.map(order => (
                  <tr key={order._id}>
                    <td className="font-mono text-xs">{order.orderNumber}</td>
                    <td className="capitalize">{order.orderType}</td>
                    <td className="font-medium">{fmtMoney(order.totalAmount)}</td>
                    <td>
                      <StatusBadge
                        variant={
                          order.status === 'completed' || order.status === 'served' ? 'success' :
                          order.status === 'preparing' ? 'warning' :
                          order.status === 'ready' ? 'primary' :
                          order.status === 'cancelled' ? 'danger' :
                          'neutral'
                        }
                        dot
                      >
                        {order.status}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
                {!loading && recentOrders.length === 0 && (
                  <tr><td colSpan="4" className="text-center text-neutral-500 py-8">No recent orders</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
